import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Topbar } from '@/components/Topbar';
import { SkipLink } from '@/components/SkipLink';
import { BuddyMascot } from '@/components/buddies';
import type { HomeworkV2, HomeworkHintV2 } from '@/data/homework-v2';
import type { HomeworkProgress, HomeworkStatus, TicketPriority } from '@/types';
import { HomeworkSurface } from '@/surfaces/HomeworkSurface';
import { AtelierEmbed } from '@/components/AtelierEmbed';
import { surfaceFilled, serializeSurface, emptySurfaceValue, checkSurface } from '@/surfaces/types';
import type { HomeworkSurfaceSpec, SurfaceCheck } from '@/surfaces/types';

/**
 * HomeworkV2View · воркбенч домашки (редизайн 21 JUN 2026 · Genie).
 * UI-эталон: 04_DESIGN/mockup_homework_workbench_v2.html + mockup_practice_hub_v2.html.
 *
 * Две панели (fill-width, не узкая колонка):
 *  - ЛЕВО «читай и делай»: header → 1 зачем → 2 задание → песочница (AtelierEmbed) →
 *    3 подсказки → разбор (locked до сдачи);
 *  - ПРАВО «Решение» (sticky, всегда на виду при прокрутке): самопроверка-чеклист +
 *    форма сдачи (surface) + «Сдать» с гейтом. Гейт = все selfCheck ✓ И surfaceFilled.
 * Mobile (≤860px): одна колонка + закреплённая снизу полоска «Решение» (над таб-баром);
 *   тап → шторка едет вверх (portal в document.body → не теряется в стек-контексте
 *   вложенных .screen / sticky / opacity-анимаций · фикс бага 24 JUN, когда fixed-лист
 *   пропадал на мобиле). Тот же инстанс контента — рендерится либо в правой панели
 *   (десктоп), либо в шторке (мобайл); состояние формы живёт в useState родителя.
 * После сдачи: чеклист/форма замораживаются, разбор раскрывается + скролл.
 * selfCheck/submission персистятся через `onPatch` (portfolio-данные).
 *
 * Контент-классы скоупятся под `.hwk-doc` (CSS · src/styles/homework-v2.css).
 */

interface HomeworkV2ViewProps {
  hw: HomeworkV2;
  /** Optional subtitle hook (old Homework.description) for the header. */
  subtitle?: string;
  hwProgress: HomeworkProgress | undefined;
  onSetStatus: (homeworkId: string, status: HomeworkStatus) => void;
  onPatch: (homeworkId: string, patch: Partial<HomeworkProgress>) => void;
  onLogo: () => void;
  /** Back to the sprint (homework tab). */
  onBack: () => void;
  /** Встроена в хаб (PracticeSpace) → НЕ рисуем свой Topbar/SkipLink (дубль). */
  embedded?: boolean;
}

const PRIORITY_CHIP: Record<TicketPriority, string> = { P0: 'p1', P1: 'p1', P2: 'p2', P3: 'p3' };

/** Реактивный матч `(max-width:860px)` · jsdom/SSR-safe (нет matchMedia → desktop). */
function useIsMobile(maxWidth = 860): boolean {
  const query = `(max-width:${maxWidth}px)`;
  const [m, setM] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return m;
}

export function HomeworkV2View({
  hw,
  subtitle,
  hwProgress,
  onSetStatus,
  onPatch,
  onLogo,
  onBack,
  embedded,
}: HomeworkV2ViewProps) {
  const submitted = hwProgress?.status === 'done';
  const [selfCheck, setSelfCheck] = useState<Record<string, boolean>>(
    () => hwProgress?.selfCheck ?? {},
  );
  const spec: HomeworkSurfaceSpec = hw.surface ?? { kind: 'freeText' };
  const [surfaceValue, setSurfaceValue] = useState<Record<string, unknown>>(() => {
    if (hwProgress?.submissionData) return hwProgress.submissionData as Record<string, unknown>;
    if (spec.kind === 'freeText' && hwProgress?.submission) return { text: hwProgress.submission };
    return emptySurfaceValue(spec);
  });
  const [surfaceOk, setSurfaceOk] = useState<boolean>(() => surfaceFilled(spec, surfaceValue));
  // Backbone #2 · результат авто-проверки (null = ещё не проверяли).
  const [checks, setChecks] = useState<SurfaceCheck[] | null>(null);
  // Мобильная шторка «Решение» (на десктопе панель всегда открыта справа).
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const razborRef = useRef<HTMLDivElement>(null);

  // Take the homework on open (locked → in-progress) so it leaves the «взять» state.
  useEffect(() => {
    if ((hwProgress?.status ?? 'locked') === 'locked') onSetStatus(hw.id, 'in-progress');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hw.id]);

  // Шторка живёт только на мобиле — закрыть при переходе на десктоп.
  useEffect(() => {
    if (!isMobile && sheetOpen) setSheetOpen(false);
  }, [isMobile, sheetOpen]);

  const checkedCount = useMemo(
    () => hw.selfCheck.reduce((n, _item, i) => n + (selfCheck[String(i)] ? 1 : 0), 0),
    [hw.selfCheck, selfCheck],
  );
  const allChecked = checkedCount === hw.selfCheck.length;
  const ready = allChecked && surfaceOk;

  const toggleCheck = (i: number) => {
    if (submitted) return;
    setSelfCheck((prev) => {
      const next = { ...prev, [String(i)]: !prev[String(i)] };
      onPatch(hw.id, { selfCheck: next });
      return next;
    });
  };

  const onSurfaceChange = (v: Record<string, unknown>, filled: boolean) => {
    if (submitted) return;
    setSurfaceValue(v);
    setSurfaceOk(filled);
    setChecks(null); // ввод изменился → прошлая проверка неактуальна
    onPatch(hw.id, { submissionData: v, submission: serializeSurface(spec, v) });
  };

  const runCheck = () => setChecks(checkSurface(spec, surfaceValue));
  const checkPassed = checks ? checks.filter((c) => c.status === 'ok').length : 0;
  const checkFails = checks ? checks.filter((c) => c.status === 'fail').length : 0;

  const submit = () => {
    if (!ready || submitted) return;
    onPatch(hw.id, {
      selfCheck,
      submissionData: surfaceValue,
      submission: serializeSurface(spec, surfaceValue),
    });
    onSetStatus(hw.id, 'done');
    setSheetOpen(false);
    // Reveal + scroll to the razbor once the DOM reflects the submitted state.
    requestAnimationFrame(() =>
      razborRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  };

  const gateMsg = ready
    ? 'Всё готово — можно сдавать'
    : `Сначала ${[
        !allChecked ? `самопроверка: ${checkedCount} из ${hw.selfCheck.length}` : null,
        !surfaceOk ? 'заполни форму задания' : null,
      ]
        .filter(Boolean)
        .join(' + ')}`;

  const solveStatus = submitted
    ? 'сдано'
    : ready
      ? 'готово к сдаче'
      : `самопроверка ${checkedCount}/${hw.selfCheck.length}${surfaceOk ? '' : ' · форма'}`;

  // ── контент «Решение» (один инстанс · десктоп-панель ИЛИ мобильная шторка) ──
  const solveBody = (
    <>
      <div className="hwk-solve-h">Самопроверка · отметь, что у тебя есть</div>
      <div className="chk" data-testid="hwk-selfcheck">
        {hw.selfCheck.map((item, i) => {
          const on = Boolean(selfCheck[String(i)]);
          return (
            <div
              key={i}
              className={`chk-row ${on ? 'on' : ''}`}
              role="checkbox"
              aria-checked={on}
              aria-disabled={submitted}
              tabIndex={submitted ? -1 : 0}
              onClick={() => toggleCheck(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCheck(i);
                }
              }}
            >
              <span className="chk-box" />
              <span className="t">{item}</span>
            </div>
          );
        })}
        <div className="chk-cnt">
          {checkedCount} из {hw.selfCheck.length}
          {allChecked ? ' — самопроверка пройдена' : ' — отметь все пункты, чтобы открыть сдачу'}
        </div>
      </div>

      <div className="hwk-solve-h">Сдача</div>
      <div className="submit-zone">
        <HomeworkSurface spec={spec} value={surfaceValue} disabled={submitted} onChange={onSurfaceChange} />
        {!submitted && (
          <div className="hwk-check" data-testid="hwk-check">
            <button
              type="button"
              className="check-btn"
              onClick={runCheck}
              data-testid="hwk-check-run"
            >
              Проверить работу
            </button>
            {checks && (
              <div className="check-out" data-testid="hwk-check-out" role="status">
                <div className={`check-sum ${checkFails === 0 ? 'ok' : 'has-fail'}`}>
                  {checkFails === 0
                    ? `Структурная проверка пройдена — ${checkPassed} ✓`
                    : `Есть что доработать — ${checkFails} важн.`}
                </div>
                {checks.map((c, i) => (
                  <div key={i} className={`check-row ${c.status}`}>
                    <span className="ci" aria-hidden="true">
                      {c.status === 'ok' ? '✓' : c.status === 'warn' ? '!' : '✕'}
                    </span>
                    <span className="ct">
                      <b>{c.label}</b>
                      {c.detail ? <span className="cd"> — {c.detail}</span> : null}
                    </span>
                  </div>
                ))}
                <div className="check-note">
                  Это авто-проверка структуры, не оценка смысла. Эталонный разбор — после сдачи.
                </div>
              </div>
            )}
          </div>
        )}
        {hw.submitNote && <div className="submit-note">{hw.submitNote}</div>}
        <button
          type="button"
          className={`sbtn ${submitted ? 'is-done' : ''}`}
          disabled={!ready || submitted}
          onClick={submit}
          data-testid="hwk-submit"
        >
          {submitted ? 'Сдано — разбор открыт' : 'Сдать домашку'}
        </button>
        {!submitted && (
          <div className="gate-msg" data-testid="hwk-gate">
            {gateMsg}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="screen hwk-screen">
      {!embedded && (
        <>
          <SkipLink />
          <Topbar
            crumb={[
              { label: 'Studio', onClick: onLogo },
              { label: `Спринт ${hw.sprintId.replace('S', '')}`, onClick: onBack },
              { label: hw.code, here: true },
            ]}
            onLogo={onLogo}
            showTimer={false}
          />
        </>
      )}
      <main
        {...(embedded ? { role: 'region', 'aria-label': 'Домашка' } : { id: 'main-content' })}
        className="hwk-main hwk-main--wb"
        tabIndex={-1}
      >
        <div className="hwk-doc hwk-wb">
          {/* ── ЛЕВО · читай и делай ─────────────────────────────── */}
          <div className="hwk-read">
            <div className="hwk-top">
              <div className="hwk-id">
                {hw.code} · домашка спринта {hw.sprintId}
              </div>
              <div className="hwk-ttl">{hw.title}</div>
              {subtitle && <div className="sub">{subtitle}</div>}
              <div className="hwk-meta">
                <span className={`chip ${PRIORITY_CHIP[hw.priority]}`}>{hw.priority}</span>
                <span className="chip time">~{hw.estimateMin} мин</span>
                {hw.portfolio && <span className="chip pf">Портфолио</span>}
              </div>
            </div>

            {/* ── 1 · зачем и что получится ────────────────────── */}
            <section className="hwk-sec">
              <div className="hwk-stag">
                <span className="num">1</span>Зачем и что получится
              </div>
              <div className="buddy" data-state={submitted ? 'happy' : 'hint'}>
                <div className="ava">
                  <BuddyMascot size={36} state={submitted ? 'happy' : 'hint'} />
                </div>
                <div className="bub">
                  <span className="tg">Buddy · совет</span>
                  {hw.buddyAdvice}
                </div>
              </div>
              <div dangerouslySetInnerHTML={{ __html: hw.brief }} />
              {hw.portfolio && (
                <div className="artefact">
                  <span className="k">Артефакт</span>
                  <span className="v">
                    {hw.portfolio.artefact} — {hw.portfolio.shows}
                  </span>
                </div>
              )}
            </section>

            {/* ── 2 · задание ──────────────────────────────────── */}
            <section className="hwk-sec">
              <div className="hwk-stag">
                <span className="num">2</span>Задание
              </div>
              {hw.given && (
                <div className="given">
                  <span className="lab">{hw.given.label}</span>
                  <span dangerouslySetInnerHTML={{ __html: hw.given.html }} />
                </div>
              )}
              <ol className="steps">
                {hw.steps.map((s, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: s }} />
                ))}
              </ol>
              {hw.example && (
                <div className="ex">
                  <div className="ex-h">{hw.example.head}</div>
                  <div className="ex-b" dangerouslySetInnerHTML={{ __html: hw.example.html }} />
                </div>
              )}
              {hw.formatNote && <p className="hwk-note">{hw.formatNote}</p>}
            </section>

            {/* ── песочница Ательера (полноширинный блок) ──────── */}
            {hw.atelier && <AtelierEmbed embed={hw.atelier} title={hw.title} />}

            {/* ── 3 · подсказки ────────────────────────────────── */}
            {hw.hints.length > 0 && (
              <section className="hwk-sec">
                <div className="hwk-stag">
                  <span className="num">3</span>Подсказки · открывай по одной, когда нужно
                </div>
                {hw.hints.map((hint, i) => (
                  <HintDrawer key={i} hint={hint} />
                ))}
              </section>
            )}

            {/* ── разбор (locked до сдачи) ─────────────────────── */}
            <section className="hwk-sec hwk-razbor-sec" ref={razborRef}>
              <div className="hwk-stag">Разбор · после сдачи</div>
              {!submitted ? (
                <div className="razbor-lock">
                  Разбор с эталонным решением и типовыми ошибками откроется после сдачи — чтобы
                  сначала подумать своей головой.
                </div>
              ) : (
                <div className="razbor open" data-testid="hwk-razbor">
                  <div className="rz-h">Эталон</div>
                  <div dangerouslySetInnerHTML={{ __html: hw.razbor.etalon }} />
                  {hw.razbor.mistakes.length > 0 && (
                    <>
                      <div className="rz-h">Типовые ошибки на этой домашке</div>
                      {hw.razbor.mistakes.map((m, i) => (
                        <div key={i} className="mistake">
                          <span className="mk">{String(i + 1).padStart(2, '0')}</span>
                          <span>{m}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {hw.razbor.qa.length > 0 && (
                    <>
                      <div className="rz-h">Вопросы на подумать · с разбором</div>
                      {hw.razbor.qa.map((pair, i) => (
                        <div key={i} className="qa-pair">
                          <span className="q">{pair.q}</span>
                          {pair.a}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* ── ПРАВО · закреплённое «Решение» (десктоп) ─────────── */}
          {!isMobile && (
            <aside className="hwk-solve">
              <div className="hwk-solve-hd hwk-solve-hd--static">
                <span className="sh-t">Решение</span>
                <span className="sh-st">{solveStatus}</span>
              </div>
              <div className="hwk-solve-body">{solveBody}</div>
            </aside>
          )}
        </div>
      </main>

      {/* ── МОБАЙЛ · закреплённая полоска + шторка (portal в body) ─── */}
      {isMobile &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <button
              type="button"
              className={`hwk-launch${submitted ? ' is-done' : ready ? ' is-ready' : ''}`}
              aria-expanded={sheetOpen}
              onClick={() => setSheetOpen((o) => !o)}
            >
              <span className="hl-t">Решение</span>
              <span className="hl-st">{solveStatus}</span>
              <span className="hl-cta">{submitted ? 'Сдано' : ready ? 'Сдать' : 'Открыть'}</span>
            </button>
            {sheetOpen && (
              <div className="hwk-sheet-scrim" onClick={() => setSheetOpen(false)}>
                <div
                  className="hwk-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Решение"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="hwk-sheet-hd">
                    <span>Решение · {solveStatus}</span>
                    <button
                      type="button"
                      className="hwk-sheet-x"
                      onClick={() => setSheetOpen(false)}
                      aria-label="Закрыть"
                    >
                      ×
                    </button>
                  </div>
                  <div className="hwk-sheet-body">
                    {/* .hwk-doc — скоуп для стилей .chk/.sbtn/.submit-zone (портал вне .hwk-doc) */}
                    <div className="hwk-doc hwk-sheet-scope">{solveBody}</div>
                  </div>
                </div>
              </div>
            )}
          </>,
          document.body,
        )}
    </div>
  );
}

function HintDrawer({ hint }: { hint: HomeworkHintV2 }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hint ${open ? 'open' : ''}`}>
      <div
        className="hint-h"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <span className="hn">{hint.label}</span>
        <span className="hs">{hint.sub}</span>
        <span className="arr">›</span>
      </div>
      <div className="hint-b" dangerouslySetInnerHTML={{ __html: hint.body }} />
    </div>
  );
}
